import { 
  Globe, 
  Server, 
  Search, 
  Calendar, 
  Lightbulb, 
  Users,
  Activity,
  TrendingUp,
  Cpu,
  Sparkles,
  GraduationCap,
  ShoppingBag
} from 'lucide-react';

import type { LucideIcon } from 'lucide-react';

export interface ServiceProcess {
  step: number;
  title: string;
  description: string;
}

export interface ServicePackage {
  name: string;
  subtitle: string;
  duration: string;
  suitedFor?: string;
  items: string[];
  recommended?: boolean;
}

export interface ServiceAgendaItem {
  time: string;
  title: string;
}

export interface ServiceRecurring {
  title: string;
  duration: string;
  intro: string;
  items: string[];
  claim: string;
}


export interface Service {
  icon: LucideIcon;
  title: string;
  slug: string;
  link?: string;
  description: string;
  /** Longer description used in listings such as the services page */
  longDescription?: string;
  heroTitle: string;
  heroDescription: string;
  /** GEO: Antwort-erst-Absatz (40–60 Wörter) direkt unter H1 */
  answerFirst?: string;
  /** GEO: Kurze, zitierfähige Kernaussagen für KI-Antworten */
  keyFacts?: string[];
  color: string;
  iconColor: string;
  features: string[];
  process: ServiceProcess[];
  benefits: string[];
  seoText: string;
  tools?: string[];
  faq?: { question: string; answer: string }[];
  /** Pfad zur Vorschaubild‑Datei (ohne Erweiterung) */
  image?: string;
  /** Optionaler Untertitel, z. B. für Schulungsprodukte */
  subtitle?: string;
  /** Zielgruppenbeschreibung */
  audience?: { intro: string; groups: string[] };
  /** Buchbare Pakete/Varianten */
  packages?: ServicePackage[];
  /** Beispielhafter Ablauf (Zeitplan) */
  agenda?: { intro: string; items: ServiceAgendaItem[]; note?: string };
  /** Lernziele einer Schulung */
  learningGoals?: string[];
  /** Was Teilnehmende bzw. Kunden erhalten */
  deliverables?: { title: string; description: string }[];
  /** Optionale Zusatzleistungen */
  addOns?: string[];
  /** Wiederkehrendes Anschlussprodukt */
  recurring?: ServiceRecurring;
  /** Rechtlicher Hinweis am Seitenende */
  legalNote?: string;
  /** Weiterführender Ratgeberartikel */
  relatedInsight?: { slug: string; title: string; teaser: string };
  /** Praxisbeispiel aus den Referenzen */
  exampleReference?: { id: string; title: string; text: string };
}


export const services: Service[] = [
  // 1. KI-Beratung
  {
    icon: Sparkles,
    title: 'KI-Beratung',
    slug: 'ki-beratung',
    description: 'Strategie, Tool-Auswahl und Integration von KI in Ihre Kernprozesse – inklusive lokaler und Open-Source-Lösungen.',
    longDescription: 'Wir begleiten kleine und mittlere Unternehmen im Harz beim sinnvollen Einsatz von KI – von der ersten Use-Case-Analyse über die Auswahl passender Tools bis zur Integration in bestehende Kernprozesse. Auf Wunsch mit datenschutzfreundlichen lokalen Modellen statt Cloud-Abhängigkeit.',
    heroTitle: 'KI-Beratung für den Mittelstand',
    heroDescription: 'Unabhängige Beratung zu Strategie, Tool-Auswahl und Integration – mit Claude, Gemini, Copilot und lokalen Open-Source-Modellen.',
    answerFirst: 'KI-Beratung von Enno & Jens hilft kleinen und mittleren Unternehmen im Harz, KI-Tools wie Claude, Gemini, Copilot und lokale Open-Source-Modelle (Ollama, Llama, Mistral) sinnvoll und DSGVO-konform in Kernprozesse zu integrieren – von der Use-Case-Analyse bis zum Rollout.',
    keyFacts: [
      'Herstellerunabhängige Beratung – kein Vendor-Lock-in',
      'DSGVO-konform durch lokale und On-Premise-Modelle möglich',
      'Erste Ergebnisse aus Pilotprojekten typisch in 4–8 Wochen',
      'Persönliche Ansprechpartner aus Wernigerode im Harz',
      'Erfahrung mit Claude, Gemini, Microsoft Copilot, Ollama, Llama, Mistral',
    ],
    color: 'from-indigo-500/20 to-violet-500/20',
    iconColor: 'text-indigo-400',
    image: '/images/services/ki-beratung',
    features: [
      'KI-Strategie & Roadmap',
      'Use-Case-Workshops für Ihr Unternehmen',
      'Tool-Auswahl (Claude, Gemini, Copilot, lokale Modelle)',
      'Integration in CRM, ERP, Marketing & Support',
      'Lokale & On-Premise-KI (Ollama, Llama, Mistral)',
      'Datenschutz- & DSGVO-Check',
      'Pilotprojekte und Proof-of-Concept',
      'Wissensaufbau und kurze Schulungen im Team',
    ],
    process: [
      { step: 1, title: 'Analyse & Use-Case-Identifikation', description: 'Wir lernen Ihre Prozesse kennen und identifizieren gemeinsam die Bereiche, in denen KI den größten Hebel bietet – ohne Hype, sondern mit Blick auf Ihren Alltag.' },
      { step: 2, title: 'Tool-Auswahl & Konzept', description: 'Wir vergleichen passende Lösungen – von Claude, Gemini und Copilot bis hin zu lokalen Open-Source-Modellen – und entwickeln ein Konzept, das zu Daten, Budget und Datenschutzanforderungen passt.' },
      { step: 3, title: 'Pilot & Integration', description: 'Wir starten mit einem überschaubaren Pilotprojekt, integrieren die KI in Ihre bestehenden Tools und Prozesse und vermitteln Ihrem Team das nötige Know-how.' },
      { step: 4, title: 'Rollout & Begleitung', description: 'Nach erfolgreichem Pilot rollen wir die Lösung schrittweise aus, messen Ergebnisse und entwickeln den KI-Einsatz in Ihrem Unternehmen kontinuierlich weiter.' },
    ],
    benefits: [
      'Zeitersparnis im Tagesgeschäft',
      'Wettbewerbsvorteil durch frühe Adaption',
      'DSGVO-konform durch lokale Modelle möglich',
      'Unabhängige Beratung – kein Vendor-Lock-in',
      'Regionale Ansprechpartner aus dem Harz',
      'Messbarer ROI durch Fokus auf Kernprozesse',
      'Niedrige Einstiegshürde durch Pilotprojekte',
    ],
    seoText: 'KI sinnvoll im Unternehmen einsetzen statt nur darüber zu reden – darum geht es bei unserer KI-Beratung. Als Agentur aus Wernigerode im Harz begleiten wir kleine und mittlere Unternehmen herstellerunabhängig: von der Use-Case-Analyse über die Auswahl der passenden Werkzeuge bis zur Integration in CRM, ERP, Marketing und Support. Wir arbeiten mit Claude, Gemini und Copilot ebenso selbstverständlich wie mit lokalen Open-Source-Modellen auf Basis von Ollama, Llama oder Mistral – ideal für Unternehmen, die ihre Daten nicht in die Cloud geben möchten. So entstehen praxistaugliche KI-Lösungen, die spürbar Zeit sparen, neue Wettbewerbsvorteile schaffen und gleichzeitig den hohen Anforderungen an Datenschutz und Datensouveränität gerecht werden.',
    tools: ['Claude', 'Gemini', 'Copilot', 'Ollama', 'Llama', 'Mistral', 'n8n', 'Open-Source-Frameworks'],
    faq: [
      { question: 'Brauchen wir eigene Server für lokale KI?', answer: 'Nicht zwingend. Für viele Anwendungsfälle reichen leistungsfähige Workstations oder ein kleiner Server. Wir beraten Sie individuell, ob Cloud, hybride Lösung oder vollständig lokale KI für Sie sinnvoll ist.' },
      { question: 'Sind unsere Unternehmensdaten bei KI-Tools sicher?', answer: 'Das hängt stark vom gewählten Tool und Setup ab. Wir prüfen für Sie Datenschutz, Vertragsbedingungen und Verarbeitungsorte und empfehlen bei sensiblen Daten gezielt lokale oder Open-Source-Modelle.' },
      { question: 'Wie schnell sehen wir erste Ergebnisse?', answer: 'In den meisten Pilotprojekten zeigen sich messbare Effekte innerhalb von 4–8 Wochen – etwa beim automatisierten Beantworten von Anfragen, der Texterstellung oder der Auswertung großer Dokumentenmengen.' },
      { question: 'Was kostet eine KI-Beratung?', answer: 'Wir starten mit einem unverbindlichen Erstgespräch. Auf dieser Basis erstellen wir Ihnen ein transparentes Angebot – entweder als überschaubares Pilotprojekt oder als laufende Beratung.' },
      { question: 'Was unterscheidet euch von großen Beratungen?', answer: 'Wir sind regional, persönlich erreichbar und herstellerunabhängig. Statt langer Strategie-Foliensätze setzen wir mit Ihnen konkrete, alltagstaugliche Lösungen um – pragmatisch und mit klarem Fokus auf den Nutzen für Ihr Unternehmen.' },
    ],
  },
  // 2. KI-Kompetenz Schulung
  {
    icon: GraduationCap,
    title: 'KI-Kompetenz-Schulung',
    slug: 'ki-kompetenz-schulung',
    subtitle: 'Praxis-Schulung gemäß Artikel 4 EU AI Act',
    description: 'Praxis-Schulung, mit der Ihr Team KI sicher und sinnvoll nutzt – inklusive Grundlagen des EU AI Act und Dokumentation der Schulungsmaßnahme.',
    longDescription: 'In zwei bis fünf Stunden lernt Ihr Team, generative KI im Arbeitsalltag sinnvoll einzusetzen: verständliche Grundlagen, gute Prompts, Umgang mit Halluzinationen, Datenschutz und Vertraulichkeit sowie die Grundlagen des EU AI Act inklusive KI-Kompetenz nach Artikel 4. Sie erhalten Unterlagen, Teilnahmebestätigungen und eine Dokumentation der Schulungsmaßnahme für Ihre Unterlagen.',
    heroTitle: 'KI-Kompetenz im Unternehmen',
    heroDescription: 'KI sicher und sinnvoll im Unternehmen einsetzen – praxisnahe Schulung für Teams von 5 bis 100 Mitarbeitenden, vor Ort im Harz oder online.',
    answerFirst: 'Die Schulung „KI-Kompetenz im Unternehmen“ von Enno & Jens vermittelt Mitarbeitenden in 2 bis 5 Stunden die praktische Nutzung generativer KI, den Umgang mit Datenschutz und Risiken sowie die Grundlagen des EU AI Act inklusive KI-Kompetenz nach Artikel 4 – mit Unterlagen, Teilnahmebestätigung und Dokumentation der Schulungsmaßnahme.',
    keyFacts: [
      'Drei Varianten: Basic (2 h), Professional (3 h), Premium (4–5 h)',
      'Für KMU und Organisationen mit 5 bis 100 Mitarbeitenden',
      'Vor Ort im Harz oder online – keine Vorkenntnisse nötig',
      'Teilnahmebestätigung und Dokumentation der Schulungsmaßnahme inklusive',
      'Praxisbeispiele aus Handwerk, Tourismus, Dienstleistung und Verwaltung',
      'Jährliches Update-Format für aktuelle Tools und Entwicklungen',
    ],
    color: 'from-amber-500/20 to-orange-500/20',
    iconColor: 'text-amber-400',
    image: '/images/services/ki-kompetenz-schulung',
    audience: {
      intro: 'Die Schulung ist für kleine und mittlere Unternehmen sowie Organisationen mit 5 bis 100 Mitarbeitenden gemacht – für Menschen, die KI im Alltag nutzen, ohne Technik studiert zu haben.',
      groups: [
        'Handwerksbetriebe',
        'Dienstleister',
        'Hotels',
        'Gastronomie',
        'Immobilien',
        'Steuerberatung',
        'Agenturen',
        'Vereine & Verbände',
        'Regionale Unternehmen',
      ],
    },
    packages: [
      {
        name: 'Basic',
        subtitle: 'KI-Grundlagen für Mitarbeitende',
        duration: '2 Stunden',
        suitedFor: 'Geeignet für kleinere Teams, die einen sicheren Einstieg suchen.',
        items: [
          'KI-Grundlagen verständlich erklärt',
          'ChatGPT & Co. im Arbeitsalltag',
          'Praktische Beispiele zum Mitmachen',
          'Prompting: bessere Ergebnisse formulieren',
          'Halluzinationen erkennen und einordnen',
          'Datenschutz und Vertraulichkeit',
          'Die wichtigsten Grundlagen des EU AI Act',
        ],
      },
      {
        name: 'Professional',
        subtitle: 'KI-Kompetenz nach Artikel 4 EU AI Act',
        duration: '3 Stunden',
        suitedFor: 'Unsere Empfehlung – das Standardformat für Unternehmen und Organisationen.',
        recommended: true,
        items: [
          'Alle Inhalte aus Basic',
          'EU AI Act ausführlich und verständlich',
          'Artikel 4: was KI-Kompetenz konkret bedeutet',
          'Sichere KI-Nutzung im Tagesgeschäft',
          'Interne Unternehmensrichtlinien für KI',
          'KI-Agenten und Automatisierung',
          'Konkrete Beispiele aus Ihrem Unternehmen',
          'Teilnahmebestätigung für alle Teilnehmenden',
          'Dokumentation der Schulungsmaßnahme',
        ],
      },
      {
        name: 'Premium',
        subtitle: 'KI-Kompetenz + individuelle Unternehmensanalyse',
        duration: 'ca. 4–5 Stunden',
        suitedFor: 'Für Unternehmen, die nach der Schulung direkt in die Umsetzung gehen wollen.',
        items: [
          'Komplette Professional-Schulung',
          'Analyse Ihrer bestehenden KI-Nutzung',
          'Identifikation konkreter KI-Use-Cases',
          'Analyse bestehender Arbeitsprozesse',
          'Entwicklung erster KI-Anwendungsfälle',
          'Empfehlungen für interne KI-Regeln',
          'Dokumentationsvorlage für Ihre Unterlagen',
          'Individuelle Abschlussbesprechung',
        ],
      },
    ],
    agenda: {
      intro: 'So läuft das Professional-Format typischerweise ab – als Beispiel für einen Vormittag im Unternehmen.',
      items: [
        { time: '09:00–09:20', title: 'Begrüßung und Einstieg' },
        { time: '09:20–09:50', title: 'Was ist generative KI?' },
        { time: '09:50–10:20', title: 'KI praktisch einsetzen' },
        { time: '10:20–10:30', title: 'Pause' },
        { time: '10:30–11:00', title: 'Risiken, Datenschutz und Vertraulichkeit' },
        { time: '11:00–11:40', title: 'EU AI Act und Artikel 4' },
        { time: '11:40–12:00', title: 'KI im eigenen Unternehmen' },
        { time: '12:00–12:15', title: 'Fragen und Abschluss' },
      ],
      note: 'Der Ablauf ist flexibel: Wir passen Reihenfolge, Beispiele und Schwerpunkte an Ihren Betrieb und Ihre Arbeitszeiten an.',
    },
    learningGoals: [
      'generative KI in eigenen Worten erklären',
      'geeignete KI-Anwendungsfälle im Betrieb erkennen',
      'bessere Prompts formulieren',
      'KI-Ergebnisse kritisch bewerten',
      'typische Halluzinationen erkennen',
      'vertrauliche Informationen besser schützen',
      'grundlegende Anforderungen des EU AI Act einordnen',
      'verstehen, was Artikel 4 für das Unternehmen bedeutet',
      'KI verantwortungsvoll im Arbeitsalltag einsetzen',
      'mögliche KI-Automatisierungen identifizieren',
    ],
    deliverables: [
      { title: 'Teilnehmer-Handout', description: 'Kompaktes Skript mit allen Inhalten der Schulung zum Nachlesen – 10 bis 15 Seiten.' },
      { title: 'KI-Quick-Guide', description: 'Eine Seite: „Die 10 wichtigsten Regeln für KI im Unternehmen“ – zum Aushängen im Büro.' },
      { title: 'Prompt-Cheat-Sheet', description: '10 bis 15 fertige Business-Prompts für Angebote, E-Mails, Texte, Auswertungen und Recherche.' },
      { title: 'KI-Checkliste', description: '„Darf ich diese Information in eine KI eingeben?“ – klare Entscheidungshilfe für den Alltag.' },
      { title: 'AI-Act-Kurzcheck', description: '10 Fragen, mit denen Sie einordnen, wo Ihr Unternehmen beim Thema KI-Kompetenz steht.' },
      { title: 'Teilnahmebestätigung', description: 'Für jede Person eine Bestätigung über Teilnahme, Datum und behandelte Inhalte.' },
      { title: 'Dokumentation für den Arbeitgeber', description: 'Übersicht zu Unternehmen, Datum, Format, Teilnehmenden, Dauer, Inhalten, Referent, behandelten KI-Systemen und Risiken sowie individuellen Anpassungen.' },
    ],
    addOns: [
      'Durchführung vor Ort – Anfahrt nach Aufwand',
      'Zusätzliche Teilnehmende gegen Pauschale',
      'Individuelle Unternehmensanalyse',
      'Entwicklung einer internen KI-Richtlinie',
      'Jährliches KI-Kompetenz-Update',
    ],
    recurring: {
      title: 'KI-Kompetenz Update',
      duration: '90 Minuten, jährlich',
      intro: 'KI verändert sich schneller als jede andere Technologie im Unternehmen. Das Update bringt Ihr Team einmal im Jahr auf den aktuellen Stand.',
      items: [
        'neue KI-Systeme und Anbieter',
        'neue Funktionen in bekannten Tools',
        'neue Risiken und typische Fehler',
        'aktuelle Entwicklungen beim EU AI Act',
        'Anpassung Ihrer internen KI-Richtlinien',
        'neue KI-Anwendungsfälle für Ihren Betrieb',
        'offene Fragen aus dem Arbeitsalltag',
      ],
      claim: 'KI-Kompetenz ist kein einmaliges Projekt, sondern ein fortlaufender Prozess.',
    },
    legalNote: 'Unsere Schulung unterstützt Unternehmen beim Aufbau und bei der Dokumentation von KI-Kompetenz im Sinne des Artikel 4 EU AI Act. Sie stellt keine Rechtsberatung und keine Zertifizierung der vollständigen rechtlichen Konformität eines Unternehmens dar.',
    relatedInsight: {
      slug: 'ki-kompetenz-eu-ai-act',
      title: 'KI-Kompetenz nach dem EU AI Act: Was Unternehmen jetzt wissen und tun müssen',
      teaser: 'Wer ist betroffen, was verlangt Artikel 4 konkret, und wie dokumentiert man KI-Kompetenz sinnvoll? Der ausführliche Ratgeber zum Thema.',
    },
    features: [
      'Verständliche KI-Grundlagen',
      'Praktische Nutzung generativer KI',
      'Prompting für den Arbeitsalltag',
      'Datenschutz und Vertraulichkeit',
      'Typische KI-Risiken und Halluzinationen',
      'Grundlagen des EU AI Act',
      'KI-Kompetenz nach Artikel 4',
      'Konkrete Anwendungsfälle im Unternehmen',
      'KI-Agenten und Automatisierung',
      'Entwicklung interner KI-Regeln',
    ],
    process: [
      { step: 1, title: 'Vorgespräch', description: 'In 20 Minuten klären wir, wie Ihr Team heute mit KI arbeitet, welche Tools im Einsatz sind und welches Format passt – Basic, Professional oder Premium.' },
      { step: 2, title: 'Individuelle Vorbereitung', description: 'Wir passen Beispiele, Prompts und Schwerpunkte an Ihre Branche und Ihre Prozesse an. Aus Ihrem Alltag werden die Übungen der Schulung.' },
      { step: 3, title: 'Schulung vor Ort oder online', description: 'Interaktive Schulung mit Live-Beispielen, gemeinsamem Ausprobieren und Raum für Fragen – ohne Fachjargon und ohne Vorkenntnisse.' },
      { step: 4, title: 'Unterlagen & Dokumentation', description: 'Sie erhalten Handout, Quick-Guide, Cheat-Sheets, Teilnahmebestätigungen und die Dokumentation der Schulungsmaßnahme für Ihre Unterlagen.' },
    ],
    benefits: [
      'Verständlich statt juristisch trocken',
      'Praxisnah mit Beispielen aus Ihrem Alltag',
      'Speziell für kleine und mittlere Unternehmen',
      'Vor Ort im Harz oder online durchführbar',
      'Keine technischen Vorkenntnisse nötig',
      'Dokumentation der Schulungsmaßnahme inklusive',
      'Individuell auf Ihren Betrieb anpassbar',
      'KI, Digitalisierung und Umsetzung aus einer Hand',
    ],
    seoText: 'Mit der Schulung „KI-Kompetenz im Unternehmen“ bringen wir Ihr Team auf einen gemeinsamen Stand: Was kann generative KI wirklich, wo liegen die Grenzen, welche Informationen dürfen in ein KI-Tool – und was verlangt der EU AI Act mit der KI-Kompetenz nach Artikel 4? Wir arbeiten mit echten Beispielen aus Ihrem Arbeitsalltag, üben gemeinsam gute Prompts, zeigen typische Halluzinationen und entwickeln erste interne Regeln für den Umgang mit KI. Auf Wunsch schauen wir zusätzlich auf KI-Agenten und Automatisierung. Nach der Schulung erhalten Sie Unterlagen, Teilnahmebestätigungen und eine Dokumentation der Schulungsmaßnahme, mit der Sie Ihre Bemühungen um KI-Kompetenz nachvollziehbar belegen können. Durchführung vor Ort im Harz oder online – verständlich, praxisnah und ohne Compliance-Vortrag.',
    tools: ['ChatGPT', 'Claude', 'Gemini', 'Microsoft Copilot', 'Lokale Modelle (Ollama, Llama, Mistral)', 'n8n'],
    faq: [
      { question: 'Sind KI-Schulungen für Unternehmen Pflicht?', answer: 'Artikel 4 EU AI Act verlangt seit dem 2. Februar 2025 ein ausreichendes Maß an KI-Kompetenz bei allen Personen, die im Auftrag eines Unternehmens KI-Systeme betreiben oder nutzen. Eine bestimmte Schulungsform schreibt das Gesetz nicht vor – eine dokumentierte Schulung ist aber der praktikabelste Weg, dieser Anforderung nachzukommen.' },
      { question: 'Erhalten wir ein Zertifikat nach dem AI Act?', answer: 'Nein, und das kann seriös auch niemand anbieten. Sie erhalten eine Teilnahmebestätigung und eine Dokumentation der Schulungsmaßnahme. Beides belegt, welche Inhalte wann vermittelt wurden – es ist keine Zertifizierung der rechtlichen Konformität Ihres Unternehmens.' },
      { question: 'Wer im Unternehmen sollte teilnehmen?', answer: 'Alle, die mit KI arbeiten oder es künftig tun – vom Büro über Vertrieb und Marketing bis zur Geschäftsführung. Sinnvoll ist eine gemischte Gruppe, weil so gleich gemeinsame Regeln für den Umgang mit KI entstehen.' },
      { question: 'Brauchen Teilnehmende Vorkenntnisse?', answer: 'Nein. Wir starten bei den Grundlagen und arbeiten mit Beispielen aus dem Arbeitsalltag. Wer schon mit ChatGPT & Co. arbeitet, nimmt aus Prompting, Risiken und Regeln trotzdem viel mit.' },
      { question: 'Findet die Schulung bei uns im Haus oder online statt?', answer: 'Beides ist möglich. Im Harz und der Region kommen wir gern zu Ihnen – deutschlandweit schulen wir online. Bei der Durchführung vor Ort berechnen wir die Anfahrt nach Aufwand.' },
      { question: 'Was kostet die Schulung?', answer: 'Der Preis richtet sich nach Format, Gruppengröße und Aufwand für die individuelle Vorbereitung. Nach einem kurzen Vorgespräch erhalten Sie ein transparentes Angebot mit Festpreis.' },
      { question: 'Wie oft sollte KI-Kompetenz aufgefrischt werden?', answer: 'Wir empfehlen ein jährliches Update von 90 Minuten. Tools, Funktionen und Risiken verändern sich schnell, und auch beim EU AI Act kommen laufend neue Anforderungen und Auslegungen hinzu.' },
    ],
  },
  // 3. Webseitenerstellung

  {

    icon: Globe,
    title: 'Webseitenerstellung',
    slug: 'webseitenerstellung',
    description: 'Individuelle, moderne Webseiten mit Fokus auf Design, Usability und Performance.',
    longDescription: 'Wir entwickeln maßgeschneiderte Webseiten, die nicht nur gut aussehen, sondern auch funktionieren. Von der ersten Konzeption über das Design bis zur technischen Umsetzung begleiten wir Sie durch den gesamten Prozess.',
    heroTitle: 'Professionelle Webseitenerstellung',
    heroDescription: 'Von der Konzeption bis zum Launch – wir entwickeln Webseiten, die begeistern und konvertieren.',
    answerFirst: 'Wir entwickeln maßgeschneiderte Webseiten für kleine und mittlere Unternehmen im Harz – responsive, schnell, suchmaschinenfreundlich und barrierearm. Ob WordPress oder individuelle React-Lösung: Fokus liegt auf klarem Design, guter Usability, Performance und einfacher Inhaltspflege.',
    keyFacts: [
      'Individuelles Design statt Baukasten-Optik',
      'WordPress, React oder Custom je nach Anforderung',
      'Barrierefreiheit nach WCAG von Anfang an mitgedacht',
      'Rechtssichere Pflichtseiten dank eRecht24-Partnerschaft',
      'Persönliche Betreuung aus Wernigerode – kein Callcenter',
    ],
    color: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-400',
    image: '/images/services/webdesign',
    features: [
      'Responsive Design für alle Geräte',
      'CMS-Integration (WordPress, Custom)',
      'Newsletter-Erstellung & Versand',
      'Barrierefreiheit nach WCAG',
      'Performance-Optimierung',
      'SEO-freundliche Struktur',
      'Moderne Animationen',
      'Kontaktformulare mit Spam-Schutz',
    ],
    process: [
      { step: 1, title: 'Erstgespräch & Analyse', description: 'Wir lernen Ihr Unternehmen, Ihre Ziele und Ihre Zielgruppe kennen. Gemeinsam definieren wir die Anforderungen an Ihre neue Webseite.' },
      { step: 2, title: 'Konzeption & Design', description: 'Basierend auf der Analyse erstellen wir ein Konzept und präsentieren Ihnen erste Designentwürfe. Ihr Feedback fließt direkt in die Gestaltung ein.' },
      { step: 3, title: 'Entwicklung & Umsetzung', description: 'Wir setzen das Design technisch um – responsive, schnell und suchmaschinenfreundlich. Regelmäßige Zwischenstände halten Sie informiert.' },
      { step: 4, title: 'Testing & Launch', description: 'Nach ausführlichen Tests auf verschiedenen Geräten und Browsern geht Ihre Webseite live. Wir übernehmen die Domain-Einrichtung.' },
    ],
    benefits: [
      'Individuelle Gestaltung',
      'Mobile Optimierung',
      'Schnelle Ladezeiten',
      'Suchmaschinenfreundlich',
      'Einfache Inhaltspflege',
      'Zukunftssichere Technologie',
      'Rechtssichere Pflichtseiten mit eRecht24-Partnerschaft',
    ],
    seoText: 'Eine professionelle Webseite ist das digitale Aushängeschild Ihres Unternehmens. Als Agentur aus Wernigerode im Harz entwickeln wir maßgeschneiderte Webseiten, die nicht nur gut aussehen, sondern auch gefunden werden und Besucher in Kunden verwandeln. Ob Unternehmenswebseite, Landingpage oder komplexes Webprojekt – wir setzen auf moderne Technologien, responsives Design und optimale Benutzerführung. Mit unserer Erfahrung in der Webseitenerstellung unterstützen wir Sie von der ersten Idee bis zum erfolgreichen Launch und darüber hinaus.',
    tools: ['WordPress', 'React', 'Tailwind CSS', 'TypeScript', 'HTML5', 'CSS3'],
  },
  // 3a. E-Commerce-Shops
  {
    icon: ShoppingBag,
    title: 'E-Commerce-Shops',
    slug: 'onlineshop-erstellung',
    description: 'Onlineshops mit WordPress/WooCommerce oder Shopify – inklusive Zahlungs-, Versand- und Rechtsintegration für den deutschen Markt.',
    longDescription: 'Wir bauen Onlineshops, die nicht nur gut aussehen, sondern verlässlich verkaufen: mit WordPress und WooCommerce oder mit Shopify. Dabei richten wir Zahlungen, Versand und alle rechtlichen Pflichtangaben für den deutschen Markt ein – vom Grundpreis über Widerrufsbelehrung bis zum Cookie-Consent. Auf Wunsch inklusive Shop-Anleitung und Einweisung, damit Sie Bestellungen, Produkte und Kunden selbstständig pflegen können.',
    heroTitle: 'Onlineshops mit WordPress & Shopify',
    heroDescription: 'Von Produktseiten über Zahlungsarten bis zum Versandlabel: Wir bauen Ihren Shop – rechtssicher und verkaufsoptimiert.',
    answerFirst: 'Enno & Jens erstellen Onlineshops für kleine und mittlere Unternehmen – auf Basis von WordPress mit WooCommerce oder mit Shopify. Wir integrieren Zahlungen (PayPal, Kreditkarte, Klarna), automatisieren DHL-Versandlabels und richten den Shop nach den rechtlichen Anforderungen des deutschen Markts ein, zum Beispiel mit Germanized Pro, Grundpreisangaben, Widerrufsbelehrung und Cookie-Consent. Auf Wunsch erhalten Sie eine Shop-Anleitung, mit der Sie alles selbst pflegen können.',
    keyFacts: [
      'Zwei Plattformen: WordPress/WooCommerce und Shopify',
      'Rechtssicher für den deutschen Markt (u. a. Germanized Pro)',
      'Zahlungen mit PayPal, Kreditkarte und Klarna',
      'Automatisierte DHL-Versandlabels',
      'Shop-Anleitung und Einweisung für Ihr Team inklusive',
      'Erweiterte Shop-Sicherheit, z. B. mit Wordfence',
    ],
    color: 'from-emerald-500/20 to-teal-500/20',
    iconColor: 'text-emerald-400',
    image: '/images/services/onlineshop',
    features: [
      'Onlineshop mit WordPress & WooCommerce',
      'Shopify-Storefront & Produktpflege',
      'Zahlungsintegration (PayPal, Kreditkarte, Klarna)',
      'Versandautomatisierung (u. a. DHL-Labels)',
      'Rechtstexte & Pflichtangaben für den deutschen Markt',
      'Cookie-Consent & Datenschutz-Einstellungen',
      'Produktimport & Varianten (Größen, Farben, Staffelpreise)',
      'Einweisung & Shop-Anleitung für Ihr Team',
    ],
    process: [
      { step: 1, title: 'Beratung & Plattformwahl', description: 'Wir klären Ihr Sortiment, Ihre Anforderungen und Ihr Budget und wählen gemeinsam die passende Plattform: WooCommerce für maximale Flexibilität auf WordPress-Basis oder Shopify für den schnellen Start mit wenig Technik-Aufwand.' },
      { step: 2, title: 'Shop-Aufbau & Design', description: 'Wir bauen den Shop auf, passen ihn an Ihr Corporate Design an und importieren Produkte, Varianten und Kategorien – inklusive sauberer Struktur für Suche und Filter.' },
      { step: 3, title: 'Zahlung, Versand & Recht', description: 'Wir richten Zahlungsanbieter (PayPal, Kreditkarte, Klarna) ein, automatisieren Versandlabels (z. B. DHL) und konfigurieren alle Pflichtangaben für den deutschen Markt: Grundpreise, Lieferzeiten, Widerrufsbelehrung, Cookie-Consent und Shop-Sicherheit.' },
      { step: 4, title: 'Test, Launch & Einweisung', description: 'Nach Testbestellungen auf allen Geräten geht Ihr Shop live. Sie erhalten eine verständliche Shop-Anleitung und eine Einweisung, damit Bestellungen und Produktpflege im Alltag funktionieren.' },
    ],
    benefits: [
      'Verkaufsstarke Produktdarstellung',
      'Zuverlässige Zahlungsabwicklung',
      'Automatisierter Versand spart Zeit',
      'Rechtssicher für den deutschen Markt',
      'Shop selbst pflegen – ohne Agentur-Aufwand',
      'Skalierbar mit Ihrem Sortiment',
      'Erweiterte Shop-Sicherheit',
    ],
    relatedInsight: {
      slug: 'onlineshop-erstellen-checkliste',
      title: 'Onlineshop erstellen: Worauf Sie bei Shopsystem, Rechtssicherheit und Versand achten müssen',
      teaser: 'WooCommerce oder Shopify, Pflichtangaben für den deutschen Markt, DHL-Labels mit Alterssichtprüfung, Ladezeit und Bedienbarkeit – der Praxis-Ratgeber mit Checkliste.',
    },
    exampleReference: {
      id: 'flywithtoni',
      title: 'Fly With Toni – Take Off Gin',
      text: 'Für Toni Eggert haben wir einen WooCommerce-Shop für Gin umgesetzt: Germanized Pro für Grundpreis, Lieferzeit und Lebensmittelangaben, automatische DHL-Labels über Shiptastic, PayPal und Kreditkarte, Wordfence-Sicherheit und Shop-Anleitungen zum Selbstpflegen.',
    },
    seoText: 'Einen Onlineshop zu bauen ist mehr als ein Design mit Warenkorb: Es braucht zuverlässige Zahlungen, automatisierten Versand und ein Setup, das den Anforderungen des deutschen Markts standhält. Wir erstellen Shops auf zwei Wegen: mit WordPress und WooCommerce für maximale Flexibilität – etwa mit Germanized Pro, Grundpreis- und Widerrufsangaben, automatisierten DHL-Versandlabels und erweiterter Sicherheit – oder mit Shopify als geradliniger, wartungsarmer Storefront. Zahlungsarten wie PayPal, Kreditkarte und Klarna richten wir vollständig ein und testen sie mit echten Testbestellungen. Zu jedem Projekt gehört eine Shop-Anleitung plus Einweisung, damit Sie Produkte, Bestellungen und Kunden selbst pflegen können. Als Agentur aus Wernigerode im Harz begleiten wir Sie von der Plattformwahl über den Launch bis zum laufenden Betrieb.',
    tools: ['WordPress', 'WooCommerce', 'Shopify', 'Germanized Pro', 'PayPal', 'Stripe', 'Klarna', 'DHL', 'Wordfence', 'Complianz'],
    faq: [
      { question: 'WooCommerce oder Shopify – was passt zu uns?', answer: 'WooCommerce (WordPress) bietet maximale Flexibilität, lässt sich tief in Ihre bestehende Webseite integrieren und hat keine laufenden Plattformkosten. Shopify punktet mit schnellem Start, wenigen Technik-Aufgaben und breiter Zahlungsintegration. Wir empfehlen abhängig von Sortiment, Team und Budget – und sagen auch ehrlich, wenn der Weg einfacher ist, als Sie denken.' },
      { question: 'Ist der Shop rechtssicher für den deutschen Markt?', answer: 'Wir richten die typischen Pflichtangaben ein: Grundpreise, Lieferzeiten, Widerrufsbelehrung, AGB- und Datenschutz-Hinweise sowie ein korrekt konfiguriertes Cookie-Consent-Tool. Bei WooCommerce arbeiten wir dafür u. a. mit Germanized Pro. Rechtstexte erstellen wir in Zusammenarbeit mit Partnern wie eRecht24.' },
      { question: 'Welche Zahlungsarten können Kunden nutzen?', answer: 'Das gängige Set für den deutschen Markt: PayPal, Kreditkarte sowie Klarna – je nach Plattform über PayPal und Stripe eingerichtet. Weitere Zahlungsarten (Rechnungskauf, Vorkasse, Sofortüberweisung) ergänzen wir auf Wunsch.' },
      { question: 'Können Sie den Versand automatisieren?', answer: 'Ja. Zum Beispiel erzeugen DHL-Versandlabels sich automatisch aus der Bestellung, inklusive Tracking-Mail an den Kunden. Das spart bei mehreren Bestellungen pro Tag spürbar Zeit und vermeidet Eingabefehler.' },
      { question: 'Wir haben schon eine WordPress-Webseite – lässt sich ein Shop ergänzen?', answer: 'Ja, genau dieser Weg ist mit WooCommerce besonders üblich: Der Shop wird in die bestehende Seite integriert, übernimmt Design und Menüführung, und Kundinnen und Kunden wechseln nahtlos zwischen Inhalte und Warenkorb.' },
      { question: 'Pflegen wir den Shop selbst oder brauchen wir euch dauerhaft?', answer: 'Beides geht. Zu jedem Shop gehören eine verständliche Anleitung und eine Einweisung für Ihr Team – danach können Sie Produkte, Preise und Bestellungen selbst verwalten. Auf Wunsch betreuen wir den Shop laufend: Updates, Sicherheit und Weiterentwicklung.' },
    ],
  },
  // 2. Webseitencheck
  {
    icon: Activity,
    title: 'Webseitencheck',
    slug: 'webseitencheck',
    link: '/webseitencheck',
    description: 'Professionelle Analyse Ihrer Webseite: Performance, Responsive Design und SEO – mit Besprechung der Ergebnisse.',
    longDescription: 'Wir prüfen Ihre Webseite auf Performance, mobile Darstellung und SEO. In einer persönlichen Besprechung gehen wir die Ergebnisse mit Ihnen durch und geben konkrete Handlungsempfehlungen.',
    heroTitle: 'Webseitencheck – Analyse & Beratung',
    heroDescription: 'Wir prüfen Ihre Webseite auf Performance, mobile Darstellung und SEO – und besprechen die Ergebnisse persönlich mit Ihnen.',
    answerFirst: 'Beim kostenlosen Webseitencheck von Enno & Jens prüfen wir Ihre Webseite auf Performance, Core Web Vitals, mobile Darstellung und SEO-Grundlagen – zusätzlich im KI-Webseitencheck, ob Ihre Seite in ChatGPT, Perplexity und Google AI Overviews gefunden und zitiert wird. Sie erhalten eine übersichtliche Auswertung mit priorisierten Empfehlungen – und wir besprechen die Ergebnisse persönlich per Video-Call oder vor Ort im Harz.',
    keyFacts: [
      '100 % kostenlos und unverbindlich',
      'Analyse von Ladezeit, Mobile-Ansicht und SEO',
      'KI-Webseitencheck: Sichtbarkeit in KI-Antworten',
      'Persönliche Besprechung inklusive – kein PDF ohne Kontext',
      'Auswertung typisch innerhalb von 1–2 Werktagen',
      'Konkrete, priorisierte Handlungsempfehlungen',
    ],
    color: 'from-teal-500/20 to-cyan-500/20',
    iconColor: 'text-teal-400',
    image: '/images/services/webseitencheck',
    features: [
      'Performance-Check (Ladezeiten, Core Web Vitals)',
      'Responsive-Check (Mobile, Tablet, Desktop)',
      'SEO-Basisanalyse (Meta-Tags, Struktur, Indexierung)',
      'KI-Webseitencheck (Sichtbarkeit in ChatGPT, Perplexity & Co.)',
      'Persönliche Besprechung der Ergebnisse',
    ],
    process: [
      { step: 1, title: 'Anfrage & Website-URL', description: 'Sie teilen uns die URL Ihrer Webseite mit. Wir analysieren sie zeitnah und vereinbaren einen Termin zur Besprechung.' },
      { step: 2, title: 'Analyse', description: 'Wir prüfen Ladezeiten, responsive Darstellung auf verschiedenen Geräten und die SEO-Grundlagen Ihrer Seite.' },
      { step: 3, title: 'Auswertung', description: 'Wir erstellen eine übersichtliche Auswertung mit konkreten Empfehlungen und Prioritäten.' },
      { step: 4, title: 'Besprechung', description: 'In einem persönlichen Gespräch (Video-Call oder vor Ort) gehen wir die Ergebnisse durch und beantworten Ihre Fragen.' },
    ],
    benefits: [
      'Transparenz über den Zustand Ihrer Webseite',
      'Konkrete Handlungsempfehlungen',
      'Priorisierte Verbesserungsvorschläge',
      'Persönliche Beratung inklusive',
    ],
    seoText: 'Ein Webseitencheck gibt Ihnen Klarheit: Wie steht es um die Performance Ihrer Webseite? Wie gut funktioniert sie auf Handy und Tablet? Und wie suchmaschinenfreundlich ist sie? Als Agentur aus Wernigerode analysieren wir Ihre Webseite professionell und besprechen die Ergebnisse persönlich mit Ihnen. So erhalten Sie nicht nur eine Analyse, sondern echte Handlungsempfehlungen für Ihr digitales Vorankommen.',
    faq: [
      { question: 'Was kostet der Webseitencheck?', answer: 'Der Webseitencheck ist kostenlos und beinhaltet die komplette Analyse sowie die persönliche Besprechung der Ergebnisse.' },
      { question: 'Wie lange dauert die Analyse?', answer: 'Die Analyse dauert in der Regel 1–2 Werktage. Die Besprechung vereinbaren wir zeitnah nach der Auswertung.' },
      { question: 'Funktioniert der Check auch für alle Arten von Webseiten?', answer: 'Ja, wir prüfen Webseiten jeder Art – von einfachen Unternehmensseiten über WordPress bis hin zu komplexen Shops oder Custom-Lösungen.' },
      { question: 'Was genau prüft ihr im Webseitencheck?', answer: 'Wir schauen uns Performance (Ladezeit, Core Web Vitals), Mobile-Darstellung, SEO-Grundlagen (Meta-Tags, Struktur, Indexierung), Barrierefreiheit auf Basisebene sowie Sicherheits- und DSGVO-Auffälligkeiten an.' },
      { question: 'Bekomme ich eine schriftliche Auswertung?', answer: 'Ja. Sie erhalten eine kompakte, priorisierte Auswertung als PDF – ergänzt durch die persönliche Besprechung, in der wir Fragen klären und die wichtigsten nächsten Schritte gemeinsam einordnen.' },
      { question: 'Bin ich nach dem Check zu weiteren Leistungen verpflichtet?', answer: 'Nein. Der Check ist unverbindlich. Wenn Sie die Empfehlungen selbst oder mit Ihrer bisherigen Agentur umsetzen möchten, ist das völlig in Ordnung – wir freuen uns natürlich, wenn Sie uns anschließend beauftragen.' },
      { question: 'Was ist der KI-Webseitencheck?', answer: 'Im KI-Webseitencheck prüfen wir, ob Ihre Webseite in KI-Antworten von ChatGPT, Perplexity, Copilot und Google AI Overviews gefunden und zitiert wird. Dazu testen wir typische Suchanfragen aus Ihrer Branche und schauen uns strukturierte Daten (JSON-LD), klare Antwortblöcke, Crawler-Freigaben (robots.txt, llms.txt) und die Konsistenz Ihrer Unternehmensdaten an.' },
    ],
  },
  // 3. SEO
  {
    icon: Search,
    title: 'Search Engine Optimization (SEO)',
    slug: 'seo-optimierung',
    description: 'Suchmaschinenoptimierung für bessere Rankings und mehr Sichtbarkeit bei Google.',
    longDescription: 'Mit gezielter Suchmaschinenoptimierung verbessern wir Ihre Auffindbarkeit bei Google und Co. Wir analysieren Ihre aktuelle Situation und entwickeln eine maßgeschneiderte SEO-Strategie.',
    heroTitle: 'SEO für bessere Google-Rankings',
    heroDescription: 'Werden Sie bei Google gefunden – mit professioneller Suchmaschinenoptimierung für nachhaltige Sichtbarkeit.',
    answerFirst: 'Mit gezieltem SEO helfen wir Unternehmen im Harz und darüber hinaus, bei Google nachhaltig besser gefunden zu werden – durch technische Optimierung, hochwertigen Content und Local SEO für Wernigerode, Halberstadt, Quedlinburg und Umgebung.',
    keyFacts: [
      'Kombination aus On-Page-, technischem und Local SEO',
      'Erste sichtbare Effekte typischerweise nach 3–6 Monaten',
      'Transparente Reports statt Blackbox',
      'Keine unseriösen Ranking-Garantien',
      'Regionaler Fokus Harz – Wernigerode, Halberstadt, Quedlinburg',
    ],
    color: 'from-orange-500/20 to-amber-500/20',
    iconColor: 'text-orange-400',
    image: '/images/services/seo',
    features: [
      'Umfassende Keyword-Analyse',
      'On-Page-Optimierung',
      'Technisches SEO',
      'Local SEO für regionale Sichtbarkeit',
      'Content-Strategie',
      'Regelmäßige Reports',
      'Wettbewerbsanalyse',
      'Google Search Console Setup',
    ],
    process: [
      { step: 1, title: 'SEO-Audit', description: 'Wir analysieren Ihre aktuelle Webseite: Wie steht es um die technische Qualität, den Content und die Backlinks? Wo liegen Potenziale?' },
      { step: 2, title: 'Keyword-Recherche', description: 'Wir identifizieren die relevanten Suchbegriffe für Ihr Geschäft – lokal und überregional. Welche Keywords haben Potenzial?' },
      { step: 3, title: 'Optimierung', description: 'On-Page-Optimierung (Texte, Meta-Tags, Struktur), technische Verbesserungen (Ladezeit, Mobile) und ggf. Content-Erstellung.' },
      { step: 4, title: 'Monitoring & Reporting', description: 'Wir überwachen Ihre Rankings, liefern regelmäßige Reports und passen die Strategie bei Bedarf an.' },
    ],
    benefits: [
      'Mehr organischer Traffic',
      'Bessere Google-Rankings',
      'Nachhaltige Sichtbarkeit',
      'Höhere Conversion-Raten',
      'Lokale Auffindbarkeit',
      'Messbare Ergebnisse',
    ],
    seoText: 'Suchmaschinenoptimierung (SEO) ist der Schlüssel zu nachhaltiger Online-Sichtbarkeit. Als SEO-Agentur aus Wernigerode helfen wir Unternehmen im Harz und darüber hinaus, bei Google besser gefunden zu werden. Mit einer Kombination aus technischem SEO, Content-Optimierung und Local SEO verbessern wir Ihre Rankings nachhaltig. Ob Handwerker, Arztpraxis oder Dienstleister – wir entwickeln eine SEO-Strategie, die zu Ihrem Geschäft passt und messbare Ergebnisse liefert.',
    faq: [
      { question: 'Wie lange dauert SEO?', answer: 'SEO ist ein langfristiger Prozess. Erste Verbesserungen sehen Sie oft nach 3–6 Monaten, nachhaltige Top-Rankings erfordern kontinuierliche Arbeit.' },
      { question: 'Garantieren Sie bestimmte Rankings?', answer: 'Seriöse SEO-Arbeit kann keine garantierten Rankings versprechen. Wir arbeiten datenbasiert und transparent, um die bestmöglichen Ergebnisse zu erzielen.' },
      { question: 'Was ist der Unterschied zwischen SEO und SEA?', answer: 'SEO liefert langfristige, organische Sichtbarkeit ohne Klickkosten. SEA (Google Ads) bringt sofort Traffic, kostet aber pro Klick. Ideal ist die Kombination: SEA für schnelle Ergebnisse, SEO für nachhaltiges Wachstum.' },
      { question: 'Was kostet SEO monatlich?', answer: 'Das hängt vom Wettbewerb, der Größe Ihrer Webseite und den Zielmärkten ab. Für kleine und mittlere Unternehmen im Harz sind monatliche Pakete im niedrigen bis mittleren dreistelligen Bereich üblich – transparent und ohne Vertragsbindung von 24 Monaten.' },
      { question: 'Was ist Local SEO und brauche ich das?', answer: 'Local SEO optimiert Ihre Sichtbarkeit für regionale Suchen wie „Steuerberater Wernigerode". Über das Google-Unternehmensprofil, konsistente NAP-Daten (Name, Adresse, Telefon) und Bewertungen. Für alle Unternehmen mit lokalem Einzugsgebiet ein Muss.' },
      { question: 'Was genau bekomme ich in den Reports?', answer: 'Monatliche Reports mit Ranking-Entwicklung Ihrer wichtigsten Keywords, organischem Traffic, Sichtbarkeitsindex, technischen Auffälligkeiten und den umgesetzten Maßnahmen – verständlich aufbereitet, ohne Agentur-Fachchinesisch.' },
    ],
  },
  // 4. SEA
  {
    icon: TrendingUp,
    title: 'Search Engine Advertising (SEA)',
    slug: 'sea',
    description: 'Gezielte Online-Werbung über Suchmaschinen (SEA) für mehr Klicks und Conversions.',
    longDescription: 'Wir erstellen und betreuen Ihre Google‑Ads-Kampagnen, optimieren Budgets und liefern detaillierte Reports. Ideal für direkte Leads und kurzfristige Sichtbarkeit.',
    heroTitle: 'Erfolgreiche Search Engine Advertising (SEA)',
    heroDescription: 'Schalten Sie professionelle Anzeigen bei Google & Co. und erreichen Sie potenzielle Kunden genau dann, wenn sie suchen.',
    answerFirst: 'Wir planen, schalten und optimieren Google-Ads-Kampagnen für kleine und mittlere Unternehmen – mit klarem Fokus auf messbare Leads, Buchungen oder Umsätze. Budget, Zielgruppen und Anzeigen bleiben jederzeit transparent und im Griff.',
    keyFacts: [
      'Sofort sichtbar bei Google – ideal für Launches und Aktionen',
      'Volle Budget-Kontrolle mit klaren Tages- und Monatsgrenzen',
      'Conversion-Tracking und Reporting inklusive',
      'Remarketing für Website-Besucher möglich',
      'Kombinierbar mit SEO für langfristige Ergebnisse',
    ],
    color: 'from-red-500/20 to-pink-500/20',
    iconColor: 'text-red-400',
    image: '/images/services/sea',
    features: [
      'Google Ads Kampagnenaufbau',
      'Suchnetzwerk- & Displayanzeigen',
      'Conversion‑Tracking & Analyse',
      'Budgetoptimierung & Reporting',
      'Remarketing',
    ],
    process: [
      { step: 1, title: 'Zieldefinition & Keywords', description: 'Wir legen Ihre Kampagnenziele fest und recherchieren passende Keywords.' },
      { step: 2, title: 'Anzeigenerstellung', description: 'Wir formulieren überzeugende Anzeigentexte und gestalten visuelle Assets.' },
      { step: 3, title: 'Setup & Tracking', description: 'Einrichtung der Kampagne im Ads-Konto inklusive Conversion-Tracking.' },
      { step: 4, title: 'Optimierung & Reporting', description: 'Kontinuierliche Auswertung und Anpassung zur Maximierung Ihres Budgets.' },
    ],
    benefits: [
      'Schnelle Sichtbarkeit',
      'Höchste Kontrolle über Budget',
      'Messbare Ergebnisse',
      'Flexible Ausrichtung',
      'Skalierbarkeit',
    ],
    seoText: 'Mit SEA erreichen Sie Kunden genau dann, wenn sie nach Ihren Produkten oder Dienstleistungen suchen. Wir erstellen und betreuen Ihre Suchmaschinenanzeigen auf Google, Bing und Co., optimieren laufend und sorgen für messbare Erfolge. Ideal für kurzfristige Kampagnen, Produktlaunches und zielgerichtete Leadgenerierung.',
  },
  // 5. GEO
  {
    icon: Cpu,
    title: 'Generative Engine Optimization (GEO)',
    slug: 'geo',
    description: 'Optimierung von Inhalten für generative Such- und KI‑Modelle (GEO).',
    longDescription: 'Bereiten Sie Ihre Inhalte auf KI-Modelle und Chatbots vor: strukturierte Daten, prompt‑freundliche Texte und semantische Ausrichtung.',
    heroTitle: 'Generative Engine Optimization (GEO)',
    heroDescription: 'Bereiten Sie Ihre Inhalte optimal für die neue Generation intelligenter Such‑ und Empfehlungsmaschinen vor.',
    answerFirst: 'GEO bereitet Ihre Inhalte so auf, dass KI-Systeme wie ChatGPT, Gemini, Perplexity und Claude Ihr Unternehmen bei Nutzerfragen zitieren – durch klare Kurzantworten, strukturierte Daten, FAQ-Blöcke, saubere Quellen und faktenreiche Marken­sprache.',
    keyFacts: [
      'Sichtbarkeit in ChatGPT, Gemini, Perplexity und Claude',
      'Antwort-erst-Struktur statt Marketing-Sprech',
      'Strukturierte Daten (Schema.org) für Maschinen lesbar',
      'FAQ- und Q&A-Blöcke als Zitat-Quellen für LLMs',
      'Synergie mit klassischem SEO statt Ersatz',
    ],
    color: 'from-yellow-500/20 to-orange-500/20',
    iconColor: 'text-yellow-400',
    image: '/images/services/geo',
    features: [
      'Prompt-optimierte Inhalte',
      'Strukturierte Daten für KI',
      'Semantische Inhaltsanalyse',
      'KI-gesteuerte Keyword‑Strategien',
      'Monitoring generativer Rankings',
    ],
    process: [
      { step: 1, title: 'Content‑Audit', description: 'Analyse vorhandener Inhalte und Identifikation von GEO-Potenzial.' },
      { step: 2, title: 'Strukturierung', description: 'Anpassung von Texten und Daten zur besseren Lesbarkeit für KI‑Modelle.' },
      { step: 3, title: 'Generative Tests', description: 'Ausspielung von Beispielen in aktuellen generativen Systemen zur Qualitätssicherung.' },
      { step: 4, title: 'Feinjustierung', description: 'Kontinuierliche Verbesserung basierend auf Feedback und Performance‑Daten.' },
    ],
    benefits: [
      'Vorreiterrolle bei neuen Suchformen',
      'Bessere Auffindbarkeit in KI-Anwendungen',
      'Zukunftssichere Content‑Strategie',
      'Synergie mit klassischem SEO',
    ],
    seoText: 'Generative Engine Optimization bereitet Ihre Inhalte auf das Zeitalter der KI-gesteuerten Suche vor. Wir sorgen dafür, dass Ihre Texte, Daten und Metadaten von Chatbots, Voice Agents und KI‑Suchmaschinen optimal interpretiert werden. Ein echter Wettbewerbsvorteil für Unternehmen, die frühzeitig auf den nächsten Evolutionsschritt im Such­marketing setzen.',
  },
  // 6. Karriereseiten
  {
    icon: Users,
    title: 'Karriereseiten',
    slug: 'karriereseiten',
    description: 'Professionelle Karriereseiten zur Mitarbeitergewinnung und Stärkung Ihrer Arbeitgebermarke.',
    longDescription: 'Im Wettbewerb um die besten Talente ist eine überzeugende Karriereseite unverzichtbar. Wir entwickeln Karriereportale, die Ihr Unternehmen als attraktiven Arbeitgeber präsentieren und qualifizierte Bewerber ansprechen.',
    heroTitle: 'Karriereseiten für Ihr Recruiting',
    heroDescription: 'Gewinnen Sie die besten Talente mit einer überzeugenden Karriereseite, die Ihre Arbeitgebermarke stärkt.',
    answerFirst: 'Wir entwickeln Karriereseiten, die Ihr Unternehmen als attraktiven Arbeitgeber im Harz zeigen – mit klarer Stellenübersicht, Employer Branding, Mitarbeiter-Testimonials und einfachem Online-Bewerbungsformular. Ziel: mehr passende Bewerbungen bei weniger Aufwand im HR-Team.',
    keyFacts: [
      'Employer-Branding-Workshop vor der Umsetzung',
      'Online-Bewerbung inkl. Anhänge, DSGVO-konform',
      'Anbindung an gängige HR-Systeme möglich',
      'Optimiert für mobile Bewerbung vom Smartphone',
      'Regionaler Fokus zieht Talente aus dem Harz an',
    ],
    color: 'from-purple-500/20 to-pink-500/20',
    iconColor: 'text-purple-400',
    image: '/images/services/karriereseiten',
    features: [
      'Stellenausschreibungen mit Filterung',
      'Online-Bewerbungsformulare',
      'Employer Branding',
      'Mitarbeiter-Testimonials',
      'Benefits-Darstellung',
      'Integration in HR-Systeme',
      'Automatische Stellenexporte',
      'Bewerbermanagement',
    ],
    process: [
      { step: 1, title: 'Employer Branding Workshop', description: 'Gemeinsam erarbeiten wir Ihre Arbeitgebermarke: Was macht Sie als Arbeitgeber einzigartig? Welche Werte und Benefits bieten Sie?' },
      { step: 2, title: 'Konzept & Content', description: 'Wir entwickeln eine Seitenstruktur, die Ihre Unternehmenskultur transportiert und Bewerber anspricht. Texte, Bilder und Videos werden geplant.' },
      { step: 3, title: 'Design & Umsetzung', description: 'Die Karriereseite wird im Einklang mit Ihrem Corporate Design entwickelt. Stellenbörse und Bewerbungsformulare werden integriert.' },
      { step: 4, title: 'Launch & Optimierung', description: 'Nach dem Launch analysieren wir die Bewerberzahlen und optimieren kontinuierlich für bessere Ergebnisse.' },
    ],
    benefits: [
      'Mehr qualifizierte Bewerber',
      'Stärkere Arbeitgebermarke',
      'Niedrigere Recruiting-Kosten',
      'Schnellere Stellenbesetzung',
      'Professioneller Auftritt',
      'Messbare Ergebnisse',
    ],
    relatedInsight: {
      slug: 'karriereseite-benefits-bewerbung',
      title: 'Karriereseite: Warum sie über Bewerbungen entscheidet – und wie Sie Benefits richtig zeigen',
      teaser: 'Was eine gute Karriereseite ausmacht, wie Benefits glaubwürdig wirken und warum die Bewerbung in zwei Minuten möglich sein sollte – mit Checkliste und FAQ.',
    },
    exampleReference: {
      id: 'mundwerk-logopaedie',
      title: 'Mundwerk – Logopädie im Harz',
      text: 'Für die Logopädie-Praxis Mundwerk in Wernigerode und Quedlinburg haben wir die Karriereseite in den Mittelpunkt gerückt: echte Einblicke per Video und Praxisfotos, Stimmen aus dem Team, Benefits auf einen Blick und ein Bewerber-Funnel, mit dem die Bewerbung in rund zwei Minuten erledigt ist.',
    },
    seoText: 'Im Wettbewerb um die besten Talente ist eine überzeugende Karriereseite unverzichtbar. Als Agentur entwickeln wir Karriereportale, die Ihr Unternehmen als attraktiven Arbeitgeber präsentieren. Von der Stellenausschreibung über Mitarbeiter-Testimonials bis zum Online-Bewerbungsformular – wir schaffen digitale Recruiting-Lösungen, die funktionieren. Ob Handwerksbetrieb, Mittelständler oder Konzern: Mit einer professionellen Karriereseite erreichen Sie passende Bewerber und senken Ihre Recruiting-Kosten nachhaltig.',
  },
  // 7. Buchungssysteme
  {
    icon: Calendar,
    title: 'Buchungssysteme',
    slug: 'buchungssysteme',
    description: 'Online-Buchungslösungen für Termine, Räume, Plätze und Tickets.',
    longDescription: 'Sparen Sie Zeit und gewinnen Sie Kunden mit einem professionellen Online-Buchungssystem. Von der Zimmerbuchung bis zur Terminvergabe – wir finden die passende Lösung.',
    heroTitle: 'Online-Buchungssysteme & Terminverwaltung',
    heroDescription: 'Sparen Sie Zeit und gewinnen Sie Kunden mit professionellen Online-Buchungslösungen für jeden Bedarf.',
    answerFirst: 'Wir integrieren passende Online-Buchungssysteme in Ihre Webseite – Calendly, SimplyBook, cal.com oder Anny für Termine, Räume und Plätze. Echtzeit-Verfügbarkeit, automatische Bestätigungen und sichere Zahlungsintegration reduzieren Telefonaufwand und Doppelbuchungen.',
    keyFacts: [
      '24/7-Buchbarkeit ohne Mehraufwand im Team',
      'Auswahl aus Calendly, SimplyBook, cal.com, Anny u. a.',
      'Automatische Bestätigungen und Erinnerungen per E-Mail',
      'Zahlung via PayPal, Kreditkarte, Klarna möglich',
      'Kalender-Sync mit Google, Outlook & iCloud',
    ],
    color: 'from-rose-500/20 to-pink-500/20',
    iconColor: 'text-rose-400',
    image: '/images/services/booking',
    features: [
      'Echtzeit-Verfügbarkeit',
      'Sichere Zahlungsintegration',
      'Automatische Bestätigungen',
      'Kalender-Synchronisation',
      'Kundenverwaltung',
      'Mehrsprachigkeit',
      'Gutschein-Systeme',
      'Statistiken & Auswertungen',
    ],
    process: [
      { step: 1, title: 'Anforderungsanalyse', description: 'Was möchten Sie buchbar machen? Termine, Räume, Kurse oder Tickets? Wir analysieren Ihren Bedarf und empfehlen die passende Lösung.' },
      { step: 2, title: 'System-Auswahl', description: 'Je nach Anforderung wählen wir die optimale Plattform: Calendly, SimplyBook, Anny oder eine individuelle Lösung.' },
      { step: 3, title: 'Integration & Anpassung', description: 'Das Buchungssystem wird in Ihre Webseite integriert, an Ihr Design angepasst und mit Zahlungsanbietern verbunden.' },
      { step: 4, title: 'Schulung & Support', description: 'Wir schulen Sie im Umgang mit dem System und stehen Ihnen bei Fragen zur Verfügung.' },
    ],
    benefits: [
      '24/7 Online-Buchungen',
      'Weniger Telefonanrufe',
      'Automatisierte Prozesse',
      'Keine Doppelbuchungen',
      'Professioneller Auftritt',
      'Zeit- und Kostenersparnis',
    ],
    seoText: 'Ein professionelles Online-Buchungssystem spart Zeit und verbessert den Service für Ihre Kunden. Als Agentur integrieren wir passende Buchungslösungen für jeden Bedarf: Terminvereinbarungen mit Calendly, SimplyBook oder cal.com für Dienstleister und Praxen, Raumbuchungen mit Anny für Coworking-Spaces und Tagungsräume sowie Platzbuchungen für Sportvereine und Fitnessanbieter. Wir beraten Sie bei der Auswahl der richtigen Lösung und integrieren das System nahtlos in Ihre Webseite.',
    tools: ['Calendly', 'SimplyBook', 'cal.com', 'Anny'],
    faq: [
      { question: 'Welches Buchungssystem ist das richtige für mich?', answer: 'Das hängt von Ihrem Anwendungsfall ab: Für einfache Terminbuchungen empfehlen wir Calendly, SimplyBook oder cal.com. Für Raumbuchungen eignet sich Anny besonders gut.' },
      { question: 'Können Kunden online bezahlen?', answer: 'Ja, alle Systeme unterstützen Online-Zahlungen via PayPal, Kreditkarte, Klarna und weitere Anbieter.' },
      { question: 'Lässt sich das Buchungssystem mit meinem Kalender synchronisieren?', answer: 'Ja. Google Kalender, Outlook/Microsoft 365 und iCloud werden von den gängigen Systemen unterstützt – Buchungen erscheinen automatisch in Ihrem Kalender und blockierte Zeiten werden für Buchungen gesperrt.' },
      { question: 'Können mehrere Mitarbeiter oder Räume verwaltet werden?', answer: 'Ja. Ob mehrere Behandler in einer Praxis, unterschiedliche Coaches, Räume oder Sportplätze – wir konfigurieren Ressourcen, Öffnungszeiten und individuelle Verfügbarkeiten pro Mitarbeiter oder Raum.' },
      { question: 'Werden Bestätigungen und Erinnerungen automatisch verschickt?', answer: 'Ja. Buchungsbestätigung, Zahlungsbestätigung und Erinnerungs-E-Mails (optional auch SMS) laufen vollständig automatisch – inklusive Kalendereinladung und Stornierungs-Link für den Kunden.' },
      { question: 'Kann ich das Buchungssystem in meine bestehende Webseite einbauen?', answer: 'Ja. Die Systeme lassen sich als Widget, Inline-Formular oder eigene Buchungsseite in Ihre Webseite integrieren – im Design Ihrer Marke, ohne dass Kunden auf externe Portale weitergeleitet werden.' },
    ],
  },
  // 8. Digitalisierung & Beratung
  {
    icon: Lightbulb,
    title: 'Digitalisierung & Beratung',
    slug: 'digitalisierung-beratung',
    description: 'Strategische Beratung zur digitalen Transformation Ihres Unternehmens.',
    longDescription: 'Die Digitalisierung bietet enorme Chancen für Ihr Unternehmen. Wir helfen Ihnen, diese zu erkennen und umzusetzen – von der Prozessoptimierung bis zur Einführung neuer Tools.',
    heroTitle: 'Digitalisierungsberatung für Ihr Unternehmen',
    heroDescription: 'Nutzen Sie die Chancen der Digitalisierung – mit strategischer Beratung und praktischer Umsetzung.',
    answerFirst: 'Wir begleiten kleine und mittlere Unternehmen im Harz auf ihrem Weg zur Digitalisierung – von der Ist-Analyse über die Auswahl passender Tools bis zur Einführung im Team. Fokus: pragmatische Lösungen, die Prozesse spürbar entlasten, statt jahrelanger Transformationsprojekte.',
    keyFacts: [
      'Pragmatisch statt Buzzword-Bingo',
      'Auswahl passender Tools statt Vendor-Lock-in',
      'Mitarbeiterschulungen und Change-Begleitung inklusive',
      'Automatisierung wiederkehrender Aufgaben (z. B. via n8n)',
      'Regionaler Ansprechpartner aus Wernigerode',
    ],
    color: 'from-yellow-500/20 to-orange-500/20',
    iconColor: 'text-yellow-400',
    image: '/images/services/beratung',
    features: [
      'Prozessanalyse & Optimierung',
      'Tool-Auswahl & Einführung',
      'Mitarbeiterschulungen',
      'Change-Management',
      'Digitale Strategie',
      'Laufende Betreuung',
      'Automatisierung',
      'Cloud-Migration',
    ],
    process: [
      { step: 1, title: 'Ist-Analyse', description: 'Wir analysieren Ihre aktuellen Prozesse, eingesetzten Tools und identifizieren Digitalisierungspotenziale.' },
      { step: 2, title: 'Strategie-Entwicklung', description: 'Basierend auf der Analyse entwickeln wir eine Digitalisierungsstrategie mit klaren Prioritäten und Meilensteinen.' },
      { step: 3, title: 'Umsetzung', description: 'Wir führen neue Tools ein, optimieren Prozesse und begleiten Ihr Team durch den Wandel – mit Schulungen und Support.' },
      { step: 4, title: 'Kontinuierliche Verbesserung', description: 'Digitalisierung ist ein fortlaufender Prozess. Wir evaluieren regelmäßig und passen die Strategie an neue Anforderungen an.' },
    ],
    benefits: [
      'Effizientere Prozesse',
      'Kostenersparnis',
      'Wettbewerbsvorteil',
      'Zufriedene Mitarbeiter',
      'Besserer Kundenservice',
      'Zukunftssicherheit',
    ],
    seoText: 'Die Digitalisierung bietet enorme Chancen für Unternehmen jeder Größe. Als Digitalisierungsberater aus dem Harz helfen wir Ihnen, diese Chancen zu nutzen – von der Prozessoptimierung über die Einführung neuer Tools bis zur umfassenden digitalen Transformation. Wir sprechen Ihre Sprache und entwickeln pragmatische Lösungen, die zu Ihrem Unternehmen passen. Ob Handwerk, Dienstleistung oder Verwaltung: Digitalisierung muss kein Mammutprojekt sein. Wir zeigen Ihnen, wie Sie Schritt für Schritt digitaler werden.',
  },
  // 9. Hosting & Betrieb
  {
    icon: Server,
    title: 'Hosting & Betrieb',
    slug: 'hosting-domains',
    description: 'Zuverlässiges Hosting und Betrieb mit schnellen Ladezeiten, Monitoring und professioneller Domain-Verwaltung.',
    longDescription: 'Unsere Hosting-Lösungen garantieren maximale Verfügbarkeit und Sicherheit für Ihre Webseite. Wir übernehmen den laufenden Betrieb inklusive Monitoring, Updates und Support. Auf Wunsch migrieren wir Ihre bestehende Webseite reibungslos – sprechen Sie uns einfach an.',
    heroTitle: 'Webhosting & Betrieb',
    heroDescription: 'Zuverlässiges Hosting, stabiler Betrieb und professionelle Domain-Verwaltung aus einer Hand.',
    answerFirst: 'Wir hosten und betreiben Webseiten für Kunden im Harz und ganz Deutschland – auf deutschen Servern, mit SSL, täglichen Backups, Monitoring und persönlichem Support. Auf Wunsch übernehmen wir die komplette Migration Ihrer bestehenden Seite inkl. Datenbank und E-Mail.',
    keyFacts: [
      'Deutsche Server, DSGVO-konformes Hosting',
      '99,9 % Uptime-Ziel mit aktivem Monitoring',
      'Tägliche automatische Backups inklusive',
      'SSL-Zertifikate, E-Mail und Domain aus einer Hand',
      'Persönlicher Ansprechpartner statt Ticket-Warteschlange',
    ],
    color: 'from-green-500/20 to-emerald-500/20',
    iconColor: 'text-green-400',
    image: '/images/services/hosting',
    features: [
      '99.9% Uptime-Garantie',
      'SSL-Zertifikate inklusive',
      'Tägliche automatische Backups',
      'Schnelle SSD-Speicher',
      'E-Mail-Hosting inklusive',
      'Domain-Registrierung',
      'Monitoring & Uptime-Checks',
      'Sicherheitsupdates',
      'Persönlicher Support',
    ],
    process: [
      { step: 1, title: 'Bedarfsanalyse', description: 'Wir ermitteln Ihre Anforderungen: Wie viel Traffic erwarten Sie? Welche E-Mail-Adressen benötigen Sie? Welche Domain-Endungen sind gewünscht?' },
      { step: 2, title: 'Setup & Konfiguration', description: 'Wir richten Ihren Hosting-Account ein, registrieren oder transferieren Domains und konfigurieren SSL-Zertifikate, E-Mail-Konten und Monitoring.' },
      { step: 3, title: 'Migration', description: 'Falls Sie bereits eine Webseite haben, übernehmen wir die komplette Migration – inkl. Datenbankumzug, E-Mail-Migration und Funktionstests.' },
      { step: 4, title: 'Monitoring & Support', description: 'Wir überwachen Ihre Webseite rund um die Uhr und stehen Ihnen bei Fragen oder Problemen schnell zur Seite.' },
    ],
    benefits: [
      'Maximale Verfügbarkeit',
      'Schnelle Ladezeiten',
      'Sichere Backups',
      'Stabiler Betrieb',
      'Persönlicher Ansprechpartner',
      'Keine Downtime',
      'Faire Preise',
    ],
    seoText: 'Zuverlässiges Webhosting und stabiler Betrieb sind das Fundament jeder erfolgreichen Webseite. Als Agentur aus dem Harz bieten wir professionelle Hosting-Lösungen mit deutschen Servern, höchster Verfügbarkeit und persönlichem Support. Wir übernehmen auf Wunsch die Migration Ihrer bestehenden Webseite – inklusive Datenbankumzug und Funktionstests – und kümmern uns anschließend um Monitoring, Updates und Sicherheit. Von der Domain-Registrierung über SSL-Zertifikate bis zum E-Mail-Hosting bekommen Sie bei uns alles aus einer Hand.',
  },
];
